import { Injectable, isDevMode } from '@angular/core';
import { LogEntry } from '../models/log-entry.model';
import { IndexedDbLogService } from './indexeddb-log.service';
import { createSecureRandomId } from '../utils/secure-random-id';

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private sessionId: string;

  constructor(private dbService: IndexedDbLogService) {
    // Generar un ID de sesión único (CSPRNG; evita Math.random / CWE-338)
    this.sessionId = `session_${Date.now()}_${createSecureRandomId()}`;
  }

  private async log(level: LogEntry['level'], message: string, context?: string, data?: any) {
    const now = new Date();
    const timestamp = now.toISOString();
    const readableTime = this.formatTime(now);

    const entry: LogEntry = {
      level,
      message,
      context,
      data,
      timestamp,
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.sessionId,
    };

    // Mostrar en consola con formato mejorado
    if (isDevMode()) {
      const styles = this.getConsoleStyles(level);
      const contextStr = context ? `[${context}]` : '';
      
      console.log(
        `%c${readableTime}%c %c${level}%c ${contextStr} ${message}`,
        'color: #888; font-weight: normal;',
        '',
        styles,
        'color: inherit; font-weight: normal;'
      );
      
      if (data) {
        console.log('📦 Data:', data);
      }
    }

    await this.dbService.addLog(entry);
  }

  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ms = date.getMilliseconds().toString().padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${ms}`;
  }

  private getConsoleStyles(level: LogEntry['level']): string {
    const styles = {
      INFO: 'color: #3b82f6; font-weight: bold;',
      WARN: 'color: #f59e0b; font-weight: bold;',
      ERROR: 'color: #ef4444; font-weight: bold;',
      DEBUG: 'color: #8b5cf6; font-weight: bold;',
    };
    return styles[level] || '';
  }

  info(message: string, context?: string, data?: any) {
    return this.log('INFO', message, context, data);
  }

  warn(message: string, context?: string, data?: any) {
    return this.log('WARN', message, context, data);
  }

  error(message: string, context?: string, data?: any) {
    return this.log('ERROR', message, context, data);
  }

  debug(message: string, context?: string, data?: any) {
    return this.log('DEBUG', message, context, data);
  }

  getAllLogs() {
    return this.dbService.getAllLogs();
  }

  clearAllLogs() {
    return this.dbService.clearLogs();
  }

  getSessionId(): string {
    return this.sessionId;
  }

  async downloadLogs() {
    const logs: LogEntry[] = await this.getAllLogs();
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${new Date().toISOString()}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
